import type { CatalogRatioHistoryData, CatalogRatioHistoryDataFilterInput } from '@gql/graphql';
import { HistoryChangeType } from '@gql/graphql';

import type { RowClassRules } from 'ag-grid-community';
import type { NestedFieldPaths } from 'ag-grid-community/dist/types/core/entities/colDef';

type THistoryColumnKeys =
    | 'groupName'
    | 'code'
    | 'name'
    | 'ratioType'
    | 'kozp'
    | 'kem'
    | 'knr'
    | 'ksp'
    | 'kmat'
    | 'ktz'
    | 'ktzzpm'
    | 'justification';

export const historyFilterColumns: {
    [key in THistoryColumnKeys]: NestedFieldPaths<CatalogRatioHistoryData, any, []>;
} = {
    groupName: 'catalogRatioGroupName',
    code: 'code',
    name: 'changeData.name',
    ratioType: 'ratioType',
    kozp: 'changeData.kozp',
    kem: 'changeData.kem',
    knr: 'changeData.knr',
    ksp: 'changeData.ksp',
    kmat: 'changeData.kmat',
    ktz: 'changeData.ktz',
    ktzzpm: 'changeData.ktzzpm',
    justification: 'changeData.justification',
};

export const historyFilterColumnsFieldNames = Object.values(historyFilterColumns);

export type TFilterStateType = 'dateFilter' | 'typeFilter' | 'columnFilters';
export type TFilterState = {
    dateFilter: [Date?, Date?] | null;
    typeFilter: HistoryChangeType | null;
    columnFilters: Record<string, any[] | null>;
};

export const getHistoryFilterValue = (filterValue: TFilterState) => {
    const columnFilters = Object.entries(filterValue?.columnFilters ?? {}).reduce(
        (acc: Array<Record<string, any>>, [key, valueArr]) => {
            if (valueArr?.length) {
                const [left, right] = key.split('.');

                const adaptedValueArr = valueArr.map((value) => {
                    if (typeof value === 'string') {
                        return value;
                    }
                    if (typeof value === 'object' && value.name) {
                        return value.name;
                    }

                    return '';
                });

                let resultValue = {
                    [left]: {
                        [right]: { in: adaptedValueArr },
                    },
                } as any;

                if (!right) {
                    resultValue = {
                        [left]: { in: adaptedValueArr },
                    };
                }

                if (right === 'hasWorkScopesHistory') {
                    resultValue = {
                        [left]: {
                            [right]: {
                                eq: true,
                            },
                        },
                    };
                }

                if (right === 'groupFullName') {
                    resultValue = {
                        [left]: {
                            or: adaptedValueArr.map((v) => ({
                                and: [
                                    {
                                        groupCode: { eq: v.groupCode },
                                    },
                                    {
                                        groupFullName: { eq: v.groupName },
                                    },
                                ],
                            })),
                        },
                    };
                }

                acc.push(resultValue);
            }

            return acc;
        },
        [],
    );

    return {
        and: [
            {
                changeDate: {
                    gte: filterValue?.dateFilter?.[0]?.toISOString(),
                },
            },
            {
                changeDate: {
                    lte: filterValue?.dateFilter?.[1]?.toISOString(),
                },
            },
            ...(filterValue?.typeFilter
                ? [
                      {
                          or: [
                              {
                                  changeType: {
                                      eq: filterValue.typeFilter,
                                  },
                              },
                          ],
                      },
                  ]
                : []),
            ...columnFilters,
        ],
    } as CatalogRatioHistoryDataFilterInput;
};

export const historyStylesRules: RowClassRules = {
    headerRow: ({ data }: { data: CatalogRatioHistoryData }) => Boolean(data?.isHeadLine),
    addedRowClass: ({ data }: { data: CatalogRatioHistoryData }) => Boolean(data?.changeType === HistoryChangeType.New),
    updatedRowClass: ({ data }: { data: CatalogRatioHistoryData }) =>
        Boolean(data?.changeType === HistoryChangeType.Updated),
    deletedRowClass: ({ data }: { data: CatalogRatioHistoryData }) =>
        Boolean(data?.changeType === HistoryChangeType.Deleted),
};
